import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { TrendUp, Package, CheckCircle, Clock, Star, Download } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import SearchFilterBar, { filterRecords } from '@/components/SearchFilterBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VendorPerformanceDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [turnaround, setTurnaround] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorKw, setVendorKw] = useState('');

  const filteredVendors = useMemo(
    () => filterRecords(vendors, { keyword: vendorKw, fields: ['vendor_name'], dateField: '_none' }),
    [vendors, vendorKw]
  );

  const filteredTurnaround = useMemo(
    () => turnaround.filter(t => !vendorKw.trim() || t.vendor_name.toLowerCase().includes(vendorKw.trim().toLowerCase())),
    [turnaround, vendorKw]
  );

  const loadData = useCallback(async () => {
    try {
      const [perfRes, turnRes] = await Promise.all([
        axios.get(`${API}/vendor-performance`, { headers: getAuthHeader() }),
        axios.get(`${API}/vendor-performance/turnaround`, { headers: getAuthHeader() }),
      ]);
      setVendors(perfRes.data);
      setTurnaround(turnRes.data || []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load vendor performance data');
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const deliveryChartData = vendors.map(v => ({
    name: v.vendor_name,
    reliability: v.delivery_reliability,
    onTime: v.on_time_deliveries,
    late: v.late_deliveries
  }));

  const priceChartData = vendors.map(v => ({
    name: v.vendor_name,
    competitiveness: v.price_competitiveness,
    avgPrice: v.average_price
  }));

  // Reshape for RadarChart: axes = metrics, each vendor gets its own Radar series.
  // All metrics normalized to 0-100 so they share a single radius scale.
  const topVendorsForRadar = vendors.slice(0, 5);
  const radarChartData = [
    { metric: 'Reliability', ...Object.fromEntries(topVendorsForRadar.map(v => [v.vendor_name, Math.max(0, v.delivery_reliability)])) },
    { metric: 'Price Edge', ...Object.fromEntries(topVendorsForRadar.map(v => [v.vendor_name, Math.max(0, Math.min(100, 50 + v.price_competitiveness))])) },
    { metric: 'Fulfillment', ...Object.fromEntries(topVendorsForRadar.map(v => [v.vendor_name, Math.max(0, v.fulfillment_rate)])) },
    { metric: 'Rating', ...Object.fromEntries(topVendorsForRadar.map(v => [v.vendor_name, (v.average_rating / 5) * 100])) },
  ];
  const RADAR_COLORS = ['#0EA5E9', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-brand mx-auto"></div>
          <p className="mt-4 text-text-secondary">Loading vendor performance data...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-text-primary">
            Vendor Performance
          </h1>
          <p className="mt-2 text-text-secondary">Track delivery reliability, price competitiveness, and fulfillment rates</p>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-border-default rounded p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
                <Package size={20} className="text-accent-brand" weight="duotone" />
              </div>
              <h3 className="font-semibold text-text-primary">Total Vendors</h3>
            </div>
            <p className="text-3xl font-bold text-accent-brand">{vendors.length}</p>
          </div>

          <div className="bg-white border border-border-default rounded p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-status-success-bg flex items-center justify-center">
                <CheckCircle size={20} className="text-status-success" weight="duotone" />
              </div>
              <h3 className="font-semibold text-text-primary">Total Orders</h3>
            </div>
            <p className="text-3xl font-bold text-status-success">
              {vendors.reduce((sum, v) => sum + v.total_orders, 0)}
            </p>
          </div>

          <div className="bg-white border border-border-default rounded p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-status-info-bg flex items-center justify-center">
                <Clock size={20} className="text-status-info" weight="duotone" />
              </div>
              <h3 className="font-semibold text-text-primary">Avg Reliability</h3>
            </div>
            <p className="text-3xl font-bold text-status-info">
              {vendors.length > 0 
                ? (vendors.reduce((sum, v) => sum + v.delivery_reliability, 0) / vendors.length).toFixed(1)
                : 0}%
            </p>
          </div>

          <div className="bg-white border border-border-default rounded p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-status-warning-bg flex items-center justify-center">
                <Star size={20} className="text-status-warning" weight="duotone" />
              </div>
              <h3 className="font-semibold text-text-primary">Avg Rating</h3>
            </div>
            <p className="text-3xl font-bold text-status-warning">
              {vendors.length > 0 
                ? (vendors.reduce((sum, v) => sum + v.average_rating, 0) / vendors.length).toFixed(1)
                : 0}/5
            </p>
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-4">
            <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="delivery" data-testid="delivery-tab">Delivery Performance</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="pricing-tab">Price Competitiveness</TabsTrigger>
            <TabsTrigger value="turnaround" data-testid="turnaround-tab">Turnaround</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="space-y-6">
              {/* Vendor Comparison Radar — plots Reliability, Price Edge, Fulfillment & Rating across up to 5 vendors */}
              <div className="bg-white border border-border-default rounded p-6">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <h2 className="text-2xl font-bold text-text-primary">Vendor Comparison</h2>
                  <p className="text-xs text-text-tertiary">All metrics normalized to a 0–100 scale · Price Edge centered on 50 (higher = cheaper than market avg)</p>
                </div>
                {topVendorsForRadar.length === 0 ? (
                  <p className="text-text-tertiary py-8 text-center">No vendor data yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={radarChartData}>
                      <PolarGrid stroke="var(--border-default)" />
                      <PolarAngleAxis dataKey="metric" stroke="var(--text-secondary)" />
                      <PolarRadiusAxis stroke="var(--text-tertiary)" domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      {topVendorsForRadar.map((v, i) => (
                        <Radar
                          key={v.vendor_name}
                          name={v.vendor_name}
                          dataKey={v.vendor_name}
                          stroke={RADAR_COLORS[i % RADAR_COLORS.length]}
                          fill={RADAR_COLORS[i % RADAR_COLORS.length]}
                          fillOpacity={0.15}
                        />
                      ))}
                    </RadarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Vendor Details Table */}
              <div className="space-y-3">
                <SearchFilterBar
                  keyword={vendorKw} onKeywordChange={setVendorKw}
                  onClear={() => setVendorKw('')}
                  placeholder="Search vendor name…"
                  testIdPrefix="vendor-perf-filter"
                />
                <div className="bg-white border border-border-default rounded">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface border-b border-border-default">
                      <tr>
                        <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Vendor</th>
                        <th className="text-center p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Orders</th>
                        <th className="text-center p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Reliability</th>
                        <th className="text-center p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Price Edge</th>
                        <th className="text-center p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Fulfillment</th>
                        <th className="text-center p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVendors.length === 0 && (
                        <tr><td colSpan="6" className="p-8 text-center text-text-tertiary">No vendors match your filters.</td></tr>
                      )}
                      {filteredVendors.map((vendor) => (
                        <tr key={vendor.vendor_name} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`vendor-row-${vendor.vendor_name}`}>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
                                <Package size={20} className="text-accent-brand" weight="duotone" />
                              </div>
                              <span className="font-medium text-text-primary">{vendor.vendor_name}</span>
                            </div>
                          </td>
                          <td className="p-4 text-center text-text-primary">{vendor.total_orders}</td>
                          <td className="p-4 text-center">
                            <span className={`font-semibold ${vendor.delivery_reliability >= 80 ? 'text-status-success' : vendor.delivery_reliability >= 60 ? 'text-status-warning' : 'text-status-destructive'}`}>
                              {vendor.delivery_reliability.toFixed(1)}%
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className={`font-semibold ${vendor.price_competitiveness > 0 ? 'text-status-success' : 'text-status-destructive'}`}>
                              {vendor.price_competitiveness > 0 ? '+' : ''}{vendor.price_competitiveness.toFixed(1)}%
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className="font-semibold text-text-primary">{vendor.fulfillment_rate.toFixed(1)}%</span>
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Star size={16} weight="fill" className="text-status-warning" />
                              <span className="font-semibold text-text-primary">{vendor.average_rating.toFixed(1)}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="delivery" className="mt-6">
            <div className="bg-white border border-border-default rounded p-6">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Delivery Reliability Comparison</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={deliveryChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
                  <XAxis dataKey="name" stroke="var(--text-secondary)" />
                  <YAxis stroke="var(--text-secondary)" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', border: '1px solid var(--border-default)' }}
                  />
                  <Legend />
                  <Bar dataKey="reliability" fill="var(--accent-brand)" name="Reliability (%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="mt-6">
            <div className="bg-white border border-border-default rounded p-6">
              <h2 className="text-2xl font-bold text-text-primary mb-4">Price Competitiveness</h2>
              <p className="text-text-secondary mb-4">Positive values indicate better pricing than market average</p>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={priceChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
                  <XAxis dataKey="name" stroke="var(--text-secondary)" />
                  <YAxis stroke="var(--text-secondary)" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', border: '1px solid var(--border-default)' }}
                    formatter={(value, name) => {
                      if (name === 'competitiveness') return [`${value.toFixed(2)}%`, 'Price Edge'];
                      return [`₹${value.toFixed(2)}`, 'Avg Price'];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="competitiveness" fill="var(--status-success)" name="Price Edge (%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>
          <TabsContent value="turnaround" className="mt-6 space-y-6">
            {turnaround.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="turnaround-leaderboard">
                {(() => {
                  const sorted = [...turnaround].sort((a, b) => a.average_days - b.average_days);
                  const best = sorted.slice(0, 3);
                  const worst = sorted.slice(-3).reverse();
                  const renderList = (list, kind) => (
                    <div className="bg-white border border-border-default rounded p-5" data-testid={`turnaround-${kind}-card`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          kind === 'best' ? 'bg-status-success-bg' : 'bg-status-destructive-bg'
                        }`}>
                          {kind === 'best'
                            ? <TrendUp size={16} className="text-status-success" weight="fill" style={{ transform: 'scaleY(-1)' }} />
                            : <TrendUp size={16} className="text-status-destructive" weight="fill" />
                          }
                        </div>
                        <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-text-secondary">
                          {kind === 'best' ? 'Fastest Vendors' : 'Slowest Vendors'}
                        </h3>
                        <span className="ml-auto text-xs text-text-tertiary">by avg turnaround</span>
                      </div>
                      <ol className="space-y-2">
                        {list.map((v, i) => (
                          <li
                            key={v.vendor_name}
                            className="flex items-center justify-between px-3 py-2 rounded bg-surface"
                            data-testid={`turnaround-${kind}-${i + 1}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                i === 0 ? 'bg-accent-brand text-white' :
                                'bg-white border border-border-default text-text-secondary'
                              }`}>
                                {i + 1}
                              </span>
                              <div className="min-w-0">
                                <p className="font-semibold text-text-primary truncate">{v.vendor_name}</p>
                                <p className="text-xs text-text-tertiary">
                                  {v.transaction_count} transaction{v.transaction_count === 1 ? '' : 's'}
                                </p>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className={`text-lg font-bold ${
                                kind === 'best' ? 'text-status-success' : 'text-status-destructive'
                              }`}>
                                {v.average_days.toFixed(1)}
                              </p>
                              <p className="text-xs text-text-tertiary">days</p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  );
                  return (
                    <>
                      {renderList(best, 'best')}
                      {renderList(worst, 'worst')}
                    </>
                  );
                })()}
              </div>
            )}
            <div className="bg-white border border-border-default rounded p-6">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <h2 className="text-2xl font-bold text-text-primary">Vendor Turnaround (PO → Invoice)</h2>
                <div className="flex items-center gap-3">
                  <p className="text-xs text-text-tertiary max-w-md">
                    Days between the PO being sent to the vendor and the invoice / supply date. Filled row averages update automatically.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={turnaround.length === 0}
                    onClick={async () => {
                      try {
                        const response = await axios.get(`${API}/vendor-performance/turnaround.csv`, {
                          headers: getAuthHeader(),
                          responseType: 'blob',
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
                        const link = document.createElement('a');
                        link.href = url;
                        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                        link.setAttribute('download', `vendor_turnaround_${stamp}.csv`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to download CSV');
                      }
                    }}
                    data-testid="download-turnaround-csv-btn"
                    title="Download the full turnaround table as CSV for quarterly vendor reviews"
                  >
                    <Download size={14} className="mr-1" weight="duotone" />
                    Download CSV
                  </Button>
                </div>
              </div>
              <div className="mb-4">
                <SearchFilterBar
                  keyword={vendorKw} onKeywordChange={setVendorKw}
                  onClear={() => setVendorKw('')}
                  placeholder="Search vendor name…"
                  testIdPrefix="turnaround-filter"
                />
              </div>
              {filteredTurnaround.length === 0 ? (
                <p className="text-text-tertiary py-8 text-center" data-testid="turnaround-empty">
                  {turnaround.length === 0
                    ? 'No turnaround data yet. As Lab / Ops upload invoices against released POs, rows will populate here.'
                    : 'No vendors match your search.'}
                </p>
              ) : (
                <div className="space-y-8">
                  {filteredTurnaround.map((vendor) => (
                    <div key={vendor.vendor_name} className="border border-border-default rounded" data-testid={`turnaround-block-${vendor.vendor_name}`}>
                      <div className="flex items-center justify-between p-4 bg-surface border-b border-border-default">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
                            <Package size={20} className="text-accent-brand" weight="duotone" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-text-primary">{vendor.vendor_name}</h3>
                            <p className="text-xs text-text-tertiary">{vendor.transaction_count} transaction{vendor.transaction_count === 1 ? '' : 's'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs uppercase tracking-[0.2em] text-text-tertiary">Avg turnaround</p>
                          <p className="text-2xl font-bold text-accent-brand" data-testid={`turnaround-avg-${vendor.vendor_name}`}>
                            {vendor.average_days.toFixed(1)} <span className="text-sm font-normal text-text-secondary">days</span>
                          </p>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-white border-b border-border-default">
                            <tr>
                              <th className="text-left p-3 text-xs font-bold uppercase tracking-[0.15em] text-text-secondary">#</th>
                              <th className="text-left p-3 text-xs font-bold uppercase tracking-[0.15em] text-text-secondary">PO Number</th>
                              <th className="text-left p-3 text-xs font-bold uppercase tracking-[0.15em] text-text-secondary">Invoice #</th>
                              <th className="text-left p-3 text-xs font-bold uppercase tracking-[0.15em] text-text-secondary">PO Receipt Date</th>
                              <th className="text-left p-3 text-xs font-bold uppercase tracking-[0.15em] text-text-secondary">Invoice / Supply Date</th>
                              <th className="text-right p-3 text-xs font-bold uppercase tracking-[0.15em] text-text-secondary">Days</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vendor.transactions.map((t) => (
                              <tr key={t.invoice_id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`turnaround-row-${t.invoice_id}`}>
                                <td className="p-3 text-text-tertiary font-mono">{t.serial}</td>
                                <td className="p-3 font-mono font-semibold text-text-primary">{t.po_number}</td>
                                <td className="p-3 font-mono text-text-secondary">{t.invoice_number}</td>
                                <td className="p-3 text-text-secondary">{new Date(t.po_receipt_date).toLocaleDateString()}</td>
                                <td className="p-3 text-text-secondary">{new Date(t.invoice_date).toLocaleDateString()}</td>
                                <td className="p-3 text-right">
                                  <span className={`font-semibold ${
                                    t.days_diff <= 7 ? 'text-status-success' :
                                    t.days_diff <= 21 ? 'text-status-warning' :
                                    'text-status-destructive'
                                  }`} data-testid={`turnaround-days-${t.invoice_id}`}>
                                    {t.days_diff}
                                  </span>
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-surface" data-testid={`turnaround-avg-row-${vendor.vendor_name}`}>
                              <td colSpan="5" className="p-3 text-right font-semibold text-text-primary uppercase text-xs tracking-[0.2em]">
                                Average turnaround
                              </td>
                              <td className="p-3 text-right">
                                <span className="text-lg font-bold text-accent-brand">
                                  {vendor.average_days.toFixed(1)}
                                </span>
                                <span className="text-xs text-text-secondary ml-1">days</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default VendorPerformanceDashboard;
